import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect } from 'react';

// Types
export interface DisplayNotification {
  id: string;
  type: 'success' | 'warning' | 'error' | 'info';
  message: string;
  displayId?: string;
  displayName?: string;
  timestamp: Date;
  read: boolean;
}

interface DisplayNotificationContextType {
  notifications: DisplayNotification[];
  unreadCount: number;
  addNotification: (notification: Omit<DisplayNotification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

// Create context with default values
const DisplayNotificationContext = createContext<DisplayNotificationContextType>({
  notifications: [],
  unreadCount: 0,
  addNotification: () => {},
  markAsRead: () => {},
  markAllAsRead: () => {},
  clearNotification: () => {},
  clearAllNotifications: () => {},
});

// Generate a unique ID for notifications
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

// Storage key for notifications
const STORAGE_KEY = 'display_notifications';

// Provider component
export const DisplayNotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Load notifications from localStorage on initial render
  const loadNotificationsFromStorage = (): DisplayNotification[] => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as DisplayNotification[];
        // Convert timestamp strings back to Date objects
        return parsed.map(notification => ({
          ...notification,
          timestamp: new Date(notification.timestamp),
        }));
      } catch (error) {
        console.error('Failed to parse stored notifications:', error);
      }
    }
    return [];
  };

  const [notifications, setNotifications] = useState<DisplayNotification[]>(loadNotificationsFromStorage);
  
  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.read).length;

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
  }, [notifications]);

  // Add a new notification
  const addNotification = useCallback((notification: Omit<DisplayNotification, 'id' | 'timestamp' | 'read'>) => {
    setNotifications(prev => [
      {
        ...notification,
        id: generateId(),
        timestamp: new Date(),
        read: false,
      },
      ...prev,
    ]);
  }, []);

  // Mark a notification as read
  const markAsRead = useCallback((id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
  }, []);

  // Clear a notification
  const clearNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications,
  };

  return (
    <DisplayNotificationContext.Provider value={value}>
      {children}
    </DisplayNotificationContext.Provider>
  );
};

// Custom hook for using notification context
export const useDisplayNotifications = () => {
  return useContext(DisplayNotificationContext);
};

export default DisplayNotificationContext; 